/**
 * ErrorAlert Component
 * Displays user-friendly error messages with optional retry functionality
 */

import { XCircle, RefreshCw, X } from 'lucide-react';
import type { ErrorMessage } from '../../utils/errorMessages';

interface ErrorAlertProps {
  /**
   * Error message object containing message and optional suggestion
   */
  error: ErrorMessage;

  /**
   * Optional retry callback
   */
  onRetry?: () => void;

  /**
   * Whether a retry operation is in progress
   */
  isRetrying?: boolean;

  /**
   * Optional dismiss callback
   */
  onDismiss?: () => void;

  /**
   * Whether the alert can be dismissed
   */
  dismissible?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * ErrorAlert displays error messages in an accessible, user-friendly format
 *
 * @example
 * <ErrorAlert
 *   error={errorMessage}
 *   onRetry={() => retry(() => api.call())}
 *   isRetrying={isRetrying}
 *   dismissible
 *   onDismiss={() => setError(null)}
 * />
 */
export function ErrorAlert({
  error,
  onRetry,
  isRetrying = false,
  onDismiss,
  dismissible = false,
  className = '',
}: ErrorAlertProps) {
  return (
    <div
      className={`bg-red-50 border-l-4 border-red-400 rounded-md p-4 ${className}`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex items-start">
        {/* Error icon */}
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>

        {/* Error content */}
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-red-800">
            {error.message}
          </p>

          {/* Suggestion */}
          {error.suggestion && (
            <p className="mt-1 text-sm text-red-700">
              {error.suggestion}
            </p>
          )}

          {/* Retry button */}
          {onRetry && (
            <div className="mt-3">
              <button
                type="button"
                onClick={onRetry}
                disabled={isRetrying}
                className="inline-flex items-center min-h-[44px] px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                aria-label="Retry operation"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isRetrying ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </button>
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <div className="ml-3 flex-shrink-0">
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-red-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              aria-label="Dismiss error message"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
